import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import { authHeader } from "../../utils/authHeader";
import Post from "../post/Post";
import Share from "../share/Share";
import PollCard from "../polls/PollCard";
import { getSpacePolls, getAllPolls } from "../../services/pollsService";
import { AuthContext } from "../../context/AuthContext";
import { API_BASE_URL, urlDeArchivo } from "../../config";
import { enMilisegundos } from "../../utils/fechas";
import { ESPACIO_ABIERTO_ID, puedeAdministrarEspacio } from "../../utils/permisosEspacio";
import "./feed.scss";

/**
 * Las publicaciones guardan la fecha como texto ISO en UTC
 * ("2026-08-27T19:40:02.346Z") y las encuestas como DATETIME de MySQL en hora
 * de Colombia ("2026-08-27 14:40:02"). `enMilisegundos` (utils/fechas.js)
 * conoce los dos formatos, así que las dos listas se pueden ordenar juntas
 * sin que una quede sistemáticamente cinco horas por delante de la otra.
 */

// `userId` activa el modo "muro personal": solo las publicaciones de esa
// persona, sin el composer de compartir, sin encuestas y sin la barra de
// filtros. Se usa desde la página de perfil. Sin ese prop, el Feed se
// comporta exactamente como siempre.
const Feed = ({ space, userId, enPerfil = false }) => {
  const { currentUser } = useContext(AuthContext);
  const { spaceId } = useParams();
  const esMuroPersonal = userId !== undefined && userId !== null;
  const [posts, setPosts] = useState([]);
  const [feedFilter, setFeedFilter] = useState("all");
  const [selectedHashtag, setSelectedHashtag] = useState("");
  const [polls, setPolls] = useState([]);

  const fetchPolls = useCallback(async () => {
    // En el muro de una persona no se listan encuestas: son de espacios.
    if (esMuroPersonal) {
      setPolls([]);
      return;
    }
    try {
      // Dentro de un espacio: solo sus encuestas. En la pantalla principal
      // (sin espacio fijo): todas, con el espacio de cada una marcado en
      // su tarjeta, para que quede claro de dónde es cada encuesta.
      const data = space?.id ? await getSpacePolls(space.id) : await getAllPolls();
      setPolls(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al cargar encuestas:", err);
    }
  }, [space?.id, esMuroPersonal]);

  useEffect(() => {
    fetchPolls();
  }, [fetchPolls]);

  // La encuesta ahora se crea desde el composer de "Compartir" (no desde
  // un botón aparte acá): cuando se crea una, se refresca la lista para
  // que aparezca si es del espacio que se está viendo.
  useEffect(() => {
    const handlePollCreated = () => {
      fetchPolls();
    };

    window.addEventListener("poll:created", handlePollCreated);
    return () => window.removeEventListener("poll:created", handlePollCreated);
  }, [fetchPolls]);

  const handlePollChange = (updatedPoll, deletedId) => {
    setPolls((prev) => {
      if (deletedId) return prev.filter((p) => p.id !== deletedId);
      if (!updatedPoll) return prev;
      return prev.map((p) => (p.id === updatedPoll.id ? updatedPoll : p));
    });
  };

  const fetchPosts = useCallback(async () => {
    // El filtrado por autor lo hace el backend (?userId=), no el navegador:
    // así el muro de una persona no obliga a descargar todo el feed.
    const url = esMuroPersonal
      ? `${API_BASE_URL}/api/posts?userId=${encodeURIComponent(userId)}`
      : `${API_BASE_URL}/api/posts`;

    const res = await fetch(url, { headers: { ...authHeader() } });
    if (!res.ok) {
      console.error("Error al cargar publicaciones:", res.status);
      setPosts([]);
      return;
    }

    const data = await res.json();
    if (esMuroPersonal) {
      setPosts(Array.isArray(data) ? data : []);
      return;
    }
    setPosts(spaceId ? data.filter((p) => p.spaceId === +spaceId) : data);
  }, [spaceId, userId, esMuroPersonal]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    const handlePostCreated = () => {
      fetchPosts();
    };

    window.addEventListener("post:created", handlePostCreated);
    return () => window.removeEventListener("post:created", handlePostCreated);
  }, [fetchPosts]);

  const isGeneralSpace = space?.id === ESPACIO_ABIERTO_ID;

  // Dentro de un espacio publica el administrador o su encargado; en el
  // inicio y en el muro general, cualquiera.
  const canShare =
    !esMuroPersonal &&
    (!spaceId || isGeneralSpace || puedeAdministrarEspacio(currentUser, spaceId));

  const extractHashtags = (text = "") => {
    const found = text.match(/#[a-zA-ZÀ-ÿ0-9_-]+/g) || [];
    return found.map((item) => item.slice(1).toLowerCase());
  };

  const mentionTokens = [
    currentUser?.username,
    currentUser?.name,
  ]
    .filter(Boolean)
    .map((value) => `@${value}`.toLowerCase());

  const availableHashtags = Array.from(
    new Set(posts.flatMap((post) => extractHashtags(post.body || "")))
  );

  // Una encuesta fijada encabeza el muro (es el uso de "fijar": que sea lo
  // primero que ve todo el mundo). Una encuesta sin fijar es una publicación
  // más y se intercala con el resto por fecha, en vez de quedarse pegada
  // arriba como pasaba antes con TODAS las encuestas, fijadas o no.
  const encuestasFijadas = polls.filter((p) => p.isPinned);
  const encuestasNormales = polls.filter((p) => !p.isPinned);

  const filteredPosts = posts.filter((post) => {
    const body = (post.body || "").toLowerCase();

    if (feedFilter === "mentions") {
      return mentionTokens.some((token) => body.includes(token));
    }

    if (feedFilter === "hashtag" && selectedHashtag) {
      return extractHashtags(post.body || "").includes(selectedHashtag.toLowerCase());
    }

    return true;
  });

  // Los filtros de "Mis etiquetados" y "Por hashtag" son sobre el texto de
  // las publicaciones; una encuesta no tiene ni menciones ni hashtags, así
  // que solo se intercala cuando se están viendo todas.
  const lineaDeTiempo = [
    ...filteredPosts.map((post) => ({
      clave: `post-${post.id}`,
      fecha: enMilisegundos(post.date),
      post,
    })),
    ...(feedFilter === "all"
      ? encuestasNormales.map((poll) => ({
          clave: `poll-${poll.id}`,
          fecha: enMilisegundos(poll.createdAt),
          poll,
        }))
      : []),
  ].sort((a, b) => b.fecha - a.fecha);

  const handleHashtagClick = (hashtag) => {
    setFeedFilter("hashtag");
    setSelectedHashtag((hashtag || "").toLowerCase());
  };

  return (
    <div className={"feed" + (enPerfil ? " feed--perfil" : "")}>
      <div className="feedWrapper">
        {/* Mostrar Share solo si está permitido */}
        {canShare && <Share />}

        {/* Info del espacio debajo del panel de compartir */}
        {space && (
          <div className="space-info-card">
            <h2>{space.name}</h2>
            <p>{space.description}</p>
            {space.image && (
              <img
                src={urlDeArchivo(space.image)}
                alt={space.name}
                className="space-image"
              />
            )}
          </div>
        )}

        {encuestasFijadas.map((poll) => (
          <PollCard key={poll.id} poll={poll} onChange={handlePollChange} />
        ))}

        {!esMuroPersonal && (
        <div className="feed-filters">
          <button
            className={`filter-btn ${feedFilter === "all" ? "active" : ""}`}
            onClick={() => {
              setFeedFilter("all");
              setSelectedHashtag("");
            }}
          >
            Todas
          </button>
          <button
            className={`filter-btn ${feedFilter === "mentions" ? "active" : ""}`}
            onClick={() => {
              setFeedFilter("mentions");
              setSelectedHashtag("");
            }}
          >
            Mis etiquetados
          </button>
          <button
            className={`filter-btn ${feedFilter === "hashtag" ? "active" : ""}`}
            onClick={() => setFeedFilter("hashtag")}
          >
            Por hashtag
          </button>

          {feedFilter === "hashtag" && (
            <div className="hashtag-chips">
              {availableHashtags.length > 0 ? (
                availableHashtags.map((tag) => (
                  <button
                    key={tag}
                    className={`hashtag-chip ${selectedHashtag === tag ? "active" : ""}`}
                    onClick={() => setSelectedHashtag(tag)}
                  >
                    #{tag}
                  </button>
                ))
              ) : (
                <span className="hashtag-empty">No hay hashtags disponibles</span>
              )}
            </div>
          )}
        </div>
        )}

        {/* Muro: publicaciones y encuestas sin fijar, mezcladas por fecha */}
        {lineaDeTiempo.length > 0 ? (
          lineaDeTiempo.map((item) =>
            item.post ? (
              <Post key={item.clave} post={item.post} onHashtagClick={handleHashtagClick} />
            ) : (
              <PollCard key={item.clave} poll={item.poll} onChange={handlePollChange} />
            )
          )
        ) : (
          <div className="noPosts">
            <center>
              {esMuroPersonal
                ? "Esta persona todavía no ha publicado nada"
                : feedFilter === "mentions"
                ? "No hay publicaciones donde te hayan etiquetado"
                : feedFilter === "hashtag"
                ? "No hay publicaciones para ese hashtag"
                : "No se encontraron publicaciones"}
            </center>
          </div>
        )}
      </div>
    </div>
  );
};

export default Feed;
